import { ParagraphEntity, ParagraphValue } from "../pages/Entity/EntityForm";
import { getPostfix, getPostfixForEntity } from "./utils";
import { getEntityPatterns } from "./emptyEntities";

export type ObjectEntity = {
    type: string,
    keyword: string,
    value: string,
    needNumberToWords: boolean,
    adminCanFill: boolean,
    clientCanFill: boolean,
    /** Если не задано — обязательное (как у обычного поля). */
    isRequired?: boolean,
    description?: string,
    postfix: string,
    formula: string,
    roundTo?: number,
    currency?: 'RUB' | 'KZT'
}

export type ObjectValue = {
    value: string,
    entities: ObjectEntity[]
}

export const POSUTKA = "Посуточная аренда"

export function addObjectFromSelectLabel(_business?: string | null): string {
    return "+ Добавить объект"
}

/** Находит объект в values по значению (id или по полю objectSearchField). В контракте в entity.value хранится id объекта. */
export function getObjectSelectedValue(object: any, value?: string) {
    if (!object || !object.values) return null;
    const val = (value !== undefined ? value : object.value) ?? '';
    const searchField = object.objectSearchField;

    // Сначала всегда пробуем найти по id (v.value) — в контракте сохраняется именно id
    const byId = object.values.find((v: any) => v.value === val)
        || object.values.find((v: any) => (v.value ?? '').trim() === (val ?? '').trim());
    if (byId) return byId;

    if (!searchField || searchField === 'id') return null;

    // Иначе ищем по значению поля поиска (на случай если где-то передают display value)
    const sv = (val ?? '').toString().trim();
    const bySearchField = object.values.find((v: any) => {
        const fieldEntity = v.entities?.find((e: any) => e.keyword === searchField);
        const fv = (fieldEntity?.value ?? v.value ?? '').toString().trim();
        return fv === sv;
    });
    return bySearchField || null;
}

export function getInnerEntityByKeyword(object: any, keyword: string): ObjectEntity | null {
    if (!object || !object.values) return null;
    const selectedValue = getObjectSelectedValue(object);
    if (!selectedValue) return null;
    return selectedValue.entities.find((e: ObjectEntity) => e.keyword === keyword) || null;
}

export function getAllEntitiesFromTemplate(template: any): any[] {
    const result: any[] = [];
    
    // Добавляем обычные поля
    result.push(...template.entities);
    
    // Добавляем внутренние поля объектов
    for (const entity of template.entities) {
        if (entity.type === "Объект" && entity.values) {
            const selectedValue = getObjectSelectedValue(entity);
            if (selectedValue) {
                // Добавляем внутренние поля с составным ключом
                result.push(...selectedValue.entities.map((innerEntity: ObjectEntity) => ({
                    ...innerEntity,
                    keyword: `${entity.keyword}.${innerEntity.keyword}`
                })));
            }
        }
    }
    
    return result;
}

// Парсит составной ключ object.field
export function parseObjectFieldKeyword(keyword: string): {objectKey: string, fieldKey: string} | null {
    const [objectKey, fieldKey] = keyword.split('.', 2);
    if (!objectKey || !fieldKey) return null;
    return {objectKey, fieldKey};
}

// Возвращает актуальное значение внутреннего поля объекта по составному ключу
export function getObjectInnerFieldValue(entities: any[], keyword: string): string | undefined {
    const parsed = parseObjectFieldKeyword(keyword);
    if (!parsed) return undefined;
    const {objectKey, fieldKey} = parsed;
    const objectEntity = entities.find(e => e.keyword === objectKey && e.type === 'Объект');
    if (!objectEntity || !Array.isArray(objectEntity.values)) return undefined;
    const selectedValue = getObjectSelectedValue(objectEntity);
    if (!selectedValue || !Array.isArray(selectedValue.entities)) return undefined;
    
    // Находим поле в values (для получения значения)
    const fieldValue = selectedValue.entities.find((e: any) => e.keyword === fieldKey);
    if (!fieldValue) return undefined;
    
    // Находим поле в entities (для получения настроек)
    const fieldEntity = objectEntity.entities?.find((e: any) => e.keyword === fieldKey);
    if (!fieldEntity) return undefined;
    
    // Получаем значение поля
    let result = fieldValue.value ?? "";
    
    // Добавляем postfix если нужно (используем настройки из entities)
    if (fieldEntity.needNumberToWords && result) {
        console.log("add")
        // Создаем объект с настройками из fieldEntity и значением из fieldValue
        const entityWithValue = {
            ...fieldEntity,
            value: fieldValue.value
        };
        const postfix = getPostfixForEntity(entityWithValue);
        result += postfix;
    }
    
    return result;
}

// Универсальный обходчик для внутренних полей объектов
export function forEachObjectInnerField(entities: any[], callback: (objectEntity: any, innerEntity: any, fullKeyword: string) => void) {
    entities.forEach(entity => {
        if (entity.type === "Объект" && Array.isArray(entity.values)) {
            entity.values.forEach((value: any) => {
                if (Array.isArray(value.entities)) {
                    value.entities.forEach((innerEntity: any) => {
                        const fullKeyword = `${entity.keyword}.${innerEntity.keyword}`;
                        callback(entity, innerEntity, fullKeyword);
                    });
                }
            });
        }
    });
}

/** Чипы в тексте показывают выбранный вариант. Остальные варианты трогать на каждый символ не нужно. */
export function forEachSelectedObjectInnerField(
    entities: any[],
    callback: (objectEntity: any, innerEntity: any, fullKeyword: string) => void,
    onlyObjectKeyword?: string,
    onlyInnerKeyword?: string,
) {
    if (!Array.isArray(entities)) {
        return
    }
    for (const entity of entities) {
        if (entity?.type !== "Объект" || !Array.isArray(entity.values)) {
            continue
        }
        if (onlyObjectKeyword && entity.keyword !== onlyObjectKeyword) {
            continue
        }
        const selected = getObjectSelectedValue(entity)
        if (!selected || !Array.isArray(selected.entities)) {
            continue
        }
        for (const innerEntity of selected.entities) {
            if (onlyInnerKeyword && innerEntity?.keyword !== onlyInnerKeyword) {
                continue
            }
            callback(entity, innerEntity, `${entity.keyword}.${innerEntity.keyword}`)
        }
    }
}

/**
 * Проверяет поля объекта на дубликаты и пустые названия
 * @param objectEntity - объект с полями (должен иметь entities)
 * @returns объект с результатами проверки: { hasErrors: boolean, errorMessage?: string }
 */
export function validateObjectEntityFields(objectEntity: any): { hasErrors: boolean, errorMessage?: string } {
    if (!objectEntity || !objectEntity.entities || !Array.isArray(objectEntity.entities)) {
        return { hasErrors: false };
    }
    
    // Проверяем на пустые названия
    const emptyKeywords = objectEntity.entities.filter((entity: any) => !entity.keyword || entity.keyword.trim() === "");
    if (emptyKeywords.length > 0) {
        return {
            hasErrors: true,
            errorMessage: "Все поля объекта должны иметь название"
        };
    }
    
    // Проверяем на дубликаты (регистронезависимо)
    const keywords = objectEntity.entities.map((entity: any) => entity.keyword.trim().toLowerCase());
    const seenKeywords = new Set<string>();
    let duplicateKeyword = "";
    
    for (let i = 0; i < keywords.length; i++) {
        const keyword = keywords[i];
        if (seenKeywords.has(keyword)) {
            // Нашли дубликат, берем оригинальное название из entities
            duplicateKeyword = objectEntity.entities[i]?.keyword || keyword;
            break;
        }
        seenKeywords.add(keyword);
    }
    
    if (duplicateKeyword) {
        return {
            hasErrors: true,
            errorMessage: `Поле "${duplicateKeyword}" дублируется. Убедитесь, что все поля объекта имеют уникальные названия`
        };
    }
    
    return { hasErrors: false };
}

/**
 * Синхронизирует структуру values[].entities с entities (шаблоном)
 * Обновляет keyword в values[].entities на основе entities, сохраняя значения
 * @param objectEntity - объект с полями и значениями
 * @returns синхронизированный объект или null, если есть ошибки
 */
export function syncObjectEntityValues(objectEntity: any): any | null {
    if (!objectEntity || !objectEntity.entities || !Array.isArray(objectEntity.entities)) {
        return objectEntity;
    }
    
    if (!objectEntity.values || !Array.isArray(objectEntity.values)) {
        return objectEntity;
    }
    
    // Синхронизируем каждое значение
    const syncedValues = objectEntity.values.map((value: any) => {
        if (!value.entities || !Array.isArray(value.entities)) {
            // Если entities нет, создаем на основе шаблона
            return {
                ...value,
                entities: objectEntity.entities.map((templateEntity: any) => ({
                    ...templateEntity,
                    value: templateEntity.value || ""
                }))
            };
        }
        
        // Синхронизируем структуру: создаем массив на основе шаблона, сохраняя значения
        const syncedEntities = objectEntity.entities.map((templateEntity: any, templateIndex: number) => {
            // Ищем соответствующее поле в value.entities
            // Сначала по индексу
            let existingEntity = value.entities[templateIndex];
            
            // Если по индексу не найдено или keyword не совпадает, ищем по keyword
            if (!existingEntity || existingEntity.keyword !== templateEntity.keyword) {
                existingEntity = value.entities.find((e: any) => e.keyword === templateEntity.keyword);
            }
            
            // Если поле найдено, обновляем его свойства из шаблона, но сохраняем value
            if (existingEntity) {
                const requiredFromTemplate =
                    'isRequired' in templateEntity ? templateEntity.isRequired : true;
                return {
                    ...existingEntity,
                    keyword: templateEntity.keyword, // Используем keyword из шаблона
                    type: templateEntity.type,
                    needNumberToWords: templateEntity.needNumberToWords,
                    adminCanFill: templateEntity.adminCanFill,
                    clientCanFill: templateEntity.clientCanFill,
                    isRequired: requiredFromTemplate,
                    description: templateEntity.description,
                    postfix: templateEntity.postfix,
                    formula: templateEntity.formula
                };
            } else {
                // Если поля нет, создаем новое на основе шаблона
                return {
                    ...templateEntity,
                    value: templateEntity.value || ""
                };
            }
        });
        
        return {
            ...value,
            entities: syncedEntities
        };
    });
    
    return {
        ...objectEntity,
        values: syncedValues
    };
}

const PRICE_FIELD_ALIASES = new Set([
    "цена",
    "цена в сутки",
    "цена за сутки",
    "стоимость",
    "стоимость в сутки",
])

export function displayEntityKeyword(keyword: string): string {
    if (!keyword) {
        return keyword
    }
    return keyword[0].toUpperCase() + keyword.slice(1)
}

export function objectInnerMatchesTopLevel(innerKeyword: string, topKeyword: string): boolean {
    const inner = (innerKeyword ?? "").trim().toLowerCase()
    const top = (topKeyword ?? "").trim().toLowerCase()
    if (!inner || !top) {
        return false
    }
    if (inner === top) {
        return true
    }
    return PRICE_FIELD_ALIASES.has(inner) && PRICE_FIELD_ALIASES.has(top)
}

/** Подставляет значения внутренних полей выбранного объекта в одноимённые поля договора. */
export function applyObjectInnerValuesToMatchingFields(entities: any[], objectEntity: any): any[] {
    if (!Array.isArray(entities) || !objectEntity) {
        return entities
    }
    const selected = getObjectSelectedValue(objectEntity)
    const inners = selected?.entities
    if (!Array.isArray(inners) || inners.length === 0) {
        return entities
    }

    return entities.map((entity: any) => {
        if (!entity || entity.keyword === objectEntity.keyword) {
            return entity
        }
        if (entity.type === "Объект" || entity.type === "Формула" || entity.type === "Абзац") {
            return entity
        }
        const inner = inners.find((item: any) => objectInnerMatchesTopLevel(item.keyword, entity.keyword))
        const innerValue = (inner?.value ?? "").toString()
        if (!innerValue || entity.value === innerValue) {
            return entity
        }
        return { ...entity, value: innerValue }
    })
}

/** Крестик окна объекта: в список попадает только заполненное название, удаление нового — без записи. */
export function shouldCommitObjectEditOnClose(discard: boolean, objectName: string): boolean {
    return !discard && objectName.trim().length > 0
}

/** Крестик окна поля: пишем в шаблон, если поле уже названо и не заблокировано. */
export function shouldCommitEntityFormOnClose(form: { keyword?: string; $disabled?: boolean }): boolean {
    return !form.$disabled && Boolean(form.keyword?.trim())
} 